import {
  calculateDeliveryPricing,
  getAddon,
  getServiceLevel,
  normalizeAddonCodes,
  normalizeServiceCode,
} from '../../../constants/deliveryPricing';

const CLASSIC_SERVICE_CODE = 'classic';
const CLASSIC_PRICE_CENTS = Math.round(getServiceLevel(CLASSIC_SERVICE_CODE).price * 100);
const SERVICE_CODE_BY_BASE_CENTS = {
  990: 'classic',
  1490: 'coordinated',
  1990: 'premium',
};

const FALLBACK_ADDON_LABELS = {
  extra_retry_after_failure: 'Reintento posterior a falla',
};

function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function isTruthy(value) {
  return value === true || value === 1 || value === '1' || value === 'true';
}

function toCentsFromAmount(value) {
  const amount = toNumber(value);
  return amount == null ? null : Math.round(amount * 100);
}

function formatSolesFromCents(cents) {
  const value = toNumber(cents);
  if (value == null) return null;
  return `S/ ${(value / 100).toFixed(2)}`;
}

function normalizeToken(value) {
  return (value || '').toString().trim().toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

function toArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return value.toString().split(/[,;|]+/);
}

function firstNonEmptyArray(...values) {
  for (const value of values) {
    const array = toArray(value).filter(Boolean);
    if (array.length) return array;
  }
  return [];
}

function normalizeAddonCodesPreservingUnknown(value) {
  const normalized = [];

  toArray(value).forEach((item) => {
    const knownCode = normalizeAddonCodes([item])[0];
    const code = knownCode || normalizeToken(item);

    if (code && !normalized.includes(code)) {
      normalized.push(code);
    }
  });

  return normalized;
}

function humanizeCode(code) {
  return normalizeToken(code)
    .split('_')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function getExplicitPricingTotalCents(delivery = {}) {
  const pricing = delivery.pricing || {};
  return (
    toNumber(delivery.pricing_total_cents) ??
    toNumber(pricing.pricing_total_cents) ??
    toCentsFromAmount(delivery.pricing_total) ??
    toCentsFromAmount(pricing.pricing_total) ??
    (isTruthy(delivery.veinte_soles) ? 2000 : null) ??
    (isTruthy(delivery.quince_soles) ? 1500 : null)
  );
}

function inferServiceCode(delivery = {}, totalCents, addonCodes = []) {
  const pricing = delivery.pricing || {};
  const rawService =
    delivery.service_level_code ??
    pricing.service_level_code ??
    delivery.service_code ??
    delivery.service_label ??
    pricing.service_label;

  const normalizedService = normalizeServiceCode(rawService, null);
  if (normalizedService) return normalizedService;

  const knownAddonCents = addonCodes.reduce((sum, code) => {
    const addon = getAddon(code);
    return sum + Math.round((addon?.price || 0) * 100);
  }, 0);
  const inferredBaseCents = totalCents == null ? null : totalCents - knownAddonCents;

  return SERVICE_CODE_BY_BASE_CENTS[inferredBaseCents] || CLASSIC_SERVICE_CODE;
}

export function getDeliveryPricingInfo(delivery = {}) {
  const pricing = delivery.pricing || {};
  const rawAddonCodes = firstNonEmptyArray(
    delivery.selected_addon_codes,
    delivery.addon_codes,
    pricing.selected_addon_codes,
    pricing.addon_codes
  );
  const addonCodes = normalizeAddonCodesPreservingUnknown(rawAddonCodes);
  const explicitTotalCents = getExplicitPricingTotalCents(delivery);
  const serviceCode = inferServiceCode(delivery, explicitTotalCents, addonCodes);
  const service = getServiceLevel(serviceCode);
  const fallbackQuote = calculateDeliveryPricing(serviceCode, addonCodes);

  const totalCents = explicitTotalCents ?? Math.round(fallbackQuote.total * 100);

  const hasServiceUpgrade = serviceCode !== CLASSIC_SERVICE_CODE;
  const hasAddons = addonCodes.length > 0;
  const hasHigherTotal = totalCents != null && totalCents > CLASSIC_PRICE_CENTS;
  const isSpecialTariff = hasServiceUpgrade || hasAddons || hasHigherTotal;

  const backendAddonLabels = firstNonEmptyArray(delivery.addon_labels, pricing.addon_labels);
  const addonLabels = addonCodes.map((code, index) => (
    backendAddonLabels[index] ||
    getAddon(code)?.label ||
    FALLBACK_ADDON_LABELS[code] ||
    humanizeCode(code)
  ));
  const backendServiceLabel = delivery.service_label || pricing.service_label;
  const serviceLabel =
    backendServiceLabel ||
    (serviceCode === CLASSIC_SERVICE_CODE && hasHigherTotal && !hasAddons
      ? 'Tarifa ajustada'
      : service.label);

  return {
    serviceCode,
    serviceLabel,
    servicePriceLabel: service.priceLabel,
    addonCodes,
    addonLabels,
    addonCount: addonCodes.length,
    totalCents,
    totalLabel: formatSolesFromCents(totalCents) || fallbackQuote.totalLabel,
    hasServiceUpgrade,
    hasAddons,
    hasHigherTotal,
    isSpecialTariff,
    badgeLabel: isSpecialTariff
      ? `${serviceLabel}${addonCodes.length ? ` +${addonCodes.length}` : ''}`
      : serviceLabel,
  };
}

export function isSpecialTariffDelivery(delivery) {
  return getDeliveryPricingInfo(delivery).isSpecialTariff;
}

export function hasDeliveryAddons(delivery) {
  return getDeliveryPricingInfo(delivery).hasAddons;
}
